import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid, Tooltip } from '@mui/material'
import { Edit, FlagCircle, Print } from '@mui/icons-material'

import {
  useGET_courseComponentsQuery, useGET_courseSelectQuery,
} from 'redux/api/coursesApi'
import {
  useDELETE_syllabusMutation,
  useGET_syllabusByIdQuery,
  usePOST_syllabusMutation,
  usePUT_syllabusMutation,
} from 'redux/api/syllabusApi'

import { Form, Formik } from 'formik'
import DialogPopup from 'components/molecules/DialogPopup'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import RichTextField from 'components/atoms/fields/RichTextField'
import SelectField from 'components/atoms/fields/SelectField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/syllabusModel'
import { crudColorMap, crudIconMap, gradeSelectData, printDiv, termSelectData, translateGradeLevel } from 'utils/helpers'

import { ActionType, ICourseData } from 'types'
import InputField from 'components/atoms/fields/InputField'
import GoalbankPage from 'pages/protected/GoalbankPage'
import { setCurriculumSelection, setGoalbankSelection } from 'redux/ui/goalbankUI'

import { AppDispatch, RootState } from 'redux/store'
import { useDispatch, useSelector } from 'react-redux'
import GoalbankSelection from 'components/utilized/goalbank/GoalbankSelection'
import SyllabusExpectationList from 'components/molecules/SyllabusExpectationList'
import SyllabusComponentList from 'components/molecules/SyllabusComponentList'

import InfoTooltip from 'components/atoms/buttons/InfoTooltip'
import SectionTitle from 'components/atoms/SectionTitle'
import SyllabusTemplate from 'components/utilized/syllabus/SyllabusTemplate'
import SyllabusPresentation from 'components/utilized/syllabus/SyllabusPresentation'
import { useGET_setupOptionsQuery } from 'redux/api/setupApi'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const SyllabusForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  
  const backToSlug = '/syllabus'
  const editSlug = `/syllabus/edit/${id}`

  const user = useSelector((state: RootState) => state['user'].user)
  
  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.syllabus')}`,
    entityId: id,
    getEntityQuery: useGET_syllabusByIdQuery,
    createEntityMutation: usePOST_syllabusMutation,
    editEntityMutation: usePUT_syllabusMutation,
    deleteEntityMutation: useDELETE_syllabusMutation,
  })

  const { data: setupData } = useGET_setupOptionsQuery(null, {skip: (['student', 'parent'].indexOf(user?.role) > -1)})
  const { data: defaultCourseComponents } = useGET_courseComponentsQuery({page: 1, default_only: 'true'})

  const {
    course, 
    title, 
    contact_information,
    course_description,
    course_prerequisites,
    course_instructions,
    course_materials,
    student_participation,
    homework_policy,
    tutorial_policy,
    absentee_policy,
    tardy_policy,
    late_work_procedures,
    absence_procedures,
    assignment_policy,
    turning_in_assignment,
    retake_policy,
    assessment_type,
    assessment_corrections,
    grading_policy,
    grading_breakdown,
    extra_credits,
    behaviour_expectations,
    consequences,
    bullying_prevention,
    student_responsibilities,
    classroom_rules,
    plagiarism,
    penalties,
    syllabus_expectations,
    syllabus_components,
  } = formModel()

  const serverValues = entity?.id
    ? {
      ...entity
    } : {}

  let formValues = {
    ...initialValues,
    ...serverValues,
  }


  if(defaultCourseComponents && formValues.syllabus_expectations.length == 0)
    formValues.syllabus_expectations = defaultCourseComponents.results.map(e => {
      return {
          title: e.title,
          amount: 0,
          amount_choice: 'per_year',
          frequency: 0,
          frequency_choice: 'per_month',
      }
    }) 

  if(defaultCourseComponents && formValues.syllabus_components.length == 0)
    formValues.syllabus_components = defaultCourseComponents.results.map(c => {
      return {
          title: c.title,
          importance: c.importance
      }
    }) 

  if(!formValues.plagiarism)
    formValues.plagiarism = `${t('common.plagiarism-default')}`


  const [goalbankOpen, setGoalbankOpen] = useState(false)
  const goalbankSelection = useSelector((state: RootState) => state['goalbank'].goalbankSelection)
  const curriculumSelection = useSelector((state: RootState) => state['goalbank'].curriculumSelection)

  useEffect(() => {
    dispatch(setGoalbankSelection(entity?.goalbank_selection ?? {}))
    dispatch(setCurriculumSelection(entity?.curriculum_selection ?? {}))
  }, [entity])
  

  
  return (
    <>
      {entity && entity.id && (['student', 'parent'].indexOf(user?.role) > -1) &&  
      
      <>
        <div id="syllabus-presentation">
          <SyllabusPresentation entity={entity} />
        </div>

        <Button
                variant='outlined'
                color='black'
                onClick={() => navigate('/student-syllabus')}
                sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px', marginTop: '20px' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
        </Button>

        {/*
        <Button
                        variant='outlined'
                        color='black'
                        onClick={ () => { printDiv('syllabus-presentation') } }
                        sx={{ minHeight: '2.5rem', width: '10rem', marginTop: '20px' }}
                      >
                        <Print style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                        {`${t('common.print')}`}
        </Button>  */ }
      </>
      }

      {!(['student', 'parent'].indexOf(user?.role) > -1) && setupData &&
      <Formik
      enableReinitialize
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {

        const data: ICourseData = {
          ...values,
          course_id: values.course?.id ?? '0',
          goalbank_selection: goalbankSelection,
          curriculum_selection: curriculumSelection,
          syllabus_expectations: values.syllabus_expectations.map(e => {
            return {
              ...e,
              amount: parseInt(e.amount),
              amount_choice: values.amount_choice?.id ?? 'per_year',
              frequency: parseInt(e.frequency),
              frequency_choice: values.frequency_choice?.id ?? 'per_month',
            }
          })
        }

        handleSubmit(action, id, data)
      }}
    >
    {({ values, setFieldValue, errors }) => {
     return (
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>

          <Grid item xs={12} lg={4}>
            <SelectField
              fieldProps={{ ...course }}
              apiCall={useGET_courseSelectQuery}
              disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} lg={5}>
            <InputField fieldProps={{ ...title }} disabled={viewOnly} />
          </Grid>

          {setupData.filter(sd => sd.slug == "syllabus_curriculum" && sd.is_enabled).length == 1 &&  <>
          <Grid item xs={12} lg={3}>
               <Button
                  onClick={() => setGoalbankOpen(true)}
                  aria-label={`${t('goalbank.use-curriculum-library')}`}
                  size='small'
                  disabled={viewOnly}
                  color='success'
                  variant='contained'
                  sx={{ boxShadow: 'none',  minHeight: '47px' }}
                >
                  <FlagCircle style={{ fontSize: '1.5rem', marginRight: '10px' }} /> {t('goalbank.use-curriculum-library')}
                </Button>
          </Grid>

          <Grid item xs={12} lg={12}>
              <GoalbankSelection goalbankSelection={goalbankSelection} curriculumSelection={curriculumSelection}/>
          </Grid> </> }



          {setupData.filter(sd => sd.slug == "syllabus_course" && sd.is_enabled).length == 1 &&  <>
            {setupData.filter(sd => sd.slug == "syllabus_contact_information" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...contact_information }}
                disabled={viewOnly}
                label={contact_information.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_course_description" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...course_description }}
                disabled={viewOnly}
                label={course_description.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_course_prerequisites" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...course_prerequisites }}
                disabled={viewOnly}
                label={course_prerequisites.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_course_instructions" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...course_instructions }}
                disabled={viewOnly}
                label={course_instructions.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_course_materials" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...course_materials }}
                disabled={viewOnly}
                label={course_materials.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_student_participation" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...student_participation }}
                disabled={viewOnly}
                label={student_participation.label}
              />
            </Grid>}
          </>}



          {setupData.filter(sd => sd.slug == "syllabus_course_policy" && sd.is_enabled).length == 1 &&  <>
            {setupData.filter(sd => sd.slug == "syllabus_homework_policy" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...homework_policy }}
                disabled={viewOnly}
                label={homework_policy.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_tutorial_policy" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...tutorial_policy }}
                disabled={viewOnly}
                label={tutorial_policy.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_absentee_policy" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...absentee_policy }}
                disabled={viewOnly}
                label={absentee_policy.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_tardy_policy" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...tardy_policy }}
                disabled={viewOnly}
                label={tardy_policy.label}
              />
            </Grid>}
            
            {setupData.filter(sd => sd.slug == "syllabus_late_work_procedures" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...late_work_procedures }}
                disabled={viewOnly}
                label={late_work_procedures.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_absence_procedures" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...absence_procedures }}
                disabled={viewOnly}
                label={absence_procedures.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_assignment_policy" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...assignment_policy }}
                disabled={viewOnly}
                label={assignment_policy.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_turning_in_assignment" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...turning_in_assignment }}
                disabled={viewOnly}
                label={turning_in_assignment.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_retake_policy" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...retake_policy }}
                disabled={viewOnly}
                label={retake_policy.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_assessment_type" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...assessment_type }}
                disabled={viewOnly}
                label={assessment_type.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_assessment_corrections" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...assessment_corrections }}
                disabled={viewOnly}
                label={assessment_corrections.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_grading_policy" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...grading_policy }}
                disabled={viewOnly}
                label={grading_policy.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_grading_breakdown" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...grading_breakdown }}
                disabled={viewOnly}
                label={grading_breakdown.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_extra_credits" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...extra_credits }}
                disabled={viewOnly}
                label={extra_credits.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_behaviour_expectations" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...behaviour_expectations }}
                disabled={viewOnly}
                label={behaviour_expectations.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_consequences" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...consequences }}
                disabled={viewOnly}
                label={consequences.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_bullying_prevention" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...bullying_prevention }}
                disabled={viewOnly}
                label={bullying_prevention.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_student_responsibilities" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...student_responsibilities }}
                disabled={viewOnly}
                label={student_responsibilities.label}
              />
            </Grid>}


            {setupData.filter(sd => sd.slug == "syllabus_classroom_rules" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...classroom_rules }}
                disabled={viewOnly}
                label={classroom_rules.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_penalties" && sd.is_enabled).length == 1 && 
            <Grid item xs={6}>
              <RichTextField
                fieldProps={{ ...penalties }}
                disabled={viewOnly}
                label={penalties.label}
              />
            </Grid>}

            {setupData.filter(sd => sd.slug == "syllabus_plagiarism" && sd.is_enabled).length == 1 && 
            <Grid item xs={12}>
              <RichTextField
                fieldProps={{ ...plagiarism }}
                disabled={viewOnly}
                label={plagiarism.label}
              />
            </Grid>}

          </>}



          <Grid item xs={12}>
            <SectionTitle text={`${t('syllabuses.academic-expectations')}`} />
          </Grid>  

          <Grid item xs={12} lg={12}>
            <SyllabusExpectationList fieldProps={{ ...syllabus_expectations }} disabled={viewOnly} />
          </Grid>   


          <Grid item xs={12}>
            <SectionTitle text={`${t('syllabuses.grading-plan')}`} />
          </Grid>  

          <Grid item xs={12} lg={12}>
            <SyllabusComponentList fieldProps={{ ...syllabus_components }} disabled={viewOnly} />
          </Grid>   


          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>


              {entity && entity.id &&
                  <Button
                        variant='outlined'
                        color='black'
                        onClick={ () => { printDiv('syllabus-template') } }
                        sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                      >
                        <Print style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                        {`${t('common.print')}`}
                  </Button>
                  }

              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>


        {entity && entity.id &&
                <Box id="syllabus-template" sx={{ display: 'none' }}>
                    <SyllabusTemplate entity={entity} />
                </Box> }

        <DialogPopup
              title={t('common.goalbank')}
              open={goalbankOpen}
              onClose={() => setGoalbankOpen(false)}
              popupWidth={'1400px'}
              >
                  <GoalbankPage hasSelection={true} setupPrefix="syllabus"
                    filters={{
                        grade_level: values.course?.grade_level?.id ?? null,
                        subject: values.course?.subject?.id ?? null,
                        domain: values.course?.domain?.id ?? null,
                  }}/>
        </DialogPopup>
      </Form>
        )
      }}
    </Formik>
    } 
    </>
  )
}
export default SyllabusForm
